/**
 * Maps & Distance Utility - React Components
 * 
 * Components:
 * - StaticMapPreview - Map preview placeholder
 * - DistanceDisplay - Distance and travel time
 * - FuelEstimatePanel - Collapsible fuel cost estimate
 * - DirectionsButton - Opens Google Maps
 * - JobLocationSection - Combined section for Job Detail page
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  MapPin,
  Navigation,
  Clock,
  Fuel,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  Car,
  Loader2,
  Settings
} from 'lucide-react';
import GlassPanel from './GlassPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Static Map Preview (placeholder until API key available)
 */
export function StaticMapPreview({ lat, lng, address, size = 'medium' }) {
  const sizeClasses = {
    small: 'h-32',
    medium: 'h-48',
    large: 'h-64'
  };

  // Mock map background - gradient with grid pattern
  return (
    <div 
      className={`relative ${sizeClasses[size]} w-full rounded-lg overflow-hidden`}
      style={{
        background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
      }}
      data-testid="static-map-preview"
    >
      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'linear-gradient(var(--border-color) 1px, transparent 1px), linear-gradient(90deg, var(--border-color) 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      />
      
      {/* Center marker */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            <MapPin size={20} className="text-white" />
          </div>
          <div 
            className="w-3 h-3 rounded-full mt-1"
            style={{ backgroundColor: 'var(--accent-primary)' }}
          />
        </div>
      </div>

      {/* Address label */}
      {address && (
        <div className="absolute bottom-2 left-2 right-2">
          <div 
            className="px-3 py-1.5 rounded-lg text-xs truncate"
            style={{ 
              backgroundColor: 'var(--glass-bg)',
              backdropFilter: 'blur(8px)',
              color: 'var(--text-primary)'
            }}
          >
            {address}
          </div>
        </div>
      )}

      {/* Mock indicator */}
      <div className="absolute top-2 right-2">
        <span 
          className="px-2 py-0.5 rounded text-xs"
          style={{
            backgroundColor: 'var(--status-warning-bg)',
            color: 'var(--status-warning)'
          }}
        >
          Preview
        </span>
      </div>
    </div>
  );
}

/**
 * Distance Display Component
 */
export function DistanceDisplay({ distanceKm, distanceMiles, travelMinutes, fromAddress }) {
  const formatDuration = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div 
      className="grid grid-cols-3 gap-4 p-4 rounded-lg"
      style={{ backgroundColor: 'var(--bg-secondary)' }}
      data-testid="distance-display"
    >
      <div className="text-center">
        <div className="flex items-center justify-center gap-1 mb-1">
          <Navigation size={14} style={{ color: 'var(--accent-primary)' }} />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Distance</span>
        </div>
        <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {distanceMiles ? `${distanceMiles} mi` : '--'}
        </div>
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {distanceKm ? `(${distanceKm} km)` : ''}
        </div>
      </div>

      <div className="text-center border-l border-r" style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex items-center justify-center gap-1 mb-1">
          <Clock size={14} style={{ color: 'var(--accent-primary)' }} />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Est. Time</span>
        </div>
        <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {formatDuration(travelMinutes)}
        </div>
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          (estimate)
        </div>
      </div>

      <div className="text-center">
        <div className="flex items-center justify-center gap-1 mb-1">
          <MapPin size={14} style={{ color: 'var(--accent-primary)' }} />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>From</span>
        </div>
        <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
          {fromAddress || 'Base'}
        </div>
      </div>
    </div>
  );
}

/**
 * Fuel Estimate Panel (collapsible)
 */
export function FuelEstimatePanel({ 
  fuelCost, 
  litresUsed, 
  fuelPrice, 
  vehicleMpg,
  isLoading = false 
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className="rounded-lg overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)' }}
      data-testid="fuel-estimate-panel"
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 transition-colors hover:bg-[var(--bg-tertiary)]"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--status-warning-bg)' }}
          >
            <Fuel size={16} style={{ color: 'var(--status-warning)' }} />
          </div>
          <div className="text-left">
            <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Fuel Cost Estimate
            </div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Advisory only
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!isLoading && fuelCost !== null && fuelCost !== undefined && (
            <div className="text-right">
              <div className="text-lg font-semibold" style={{ color: 'var(--status-warning)' }}>
                ~£{fuelCost?.toFixed(2)}
              </div>
            </div>
          )}
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={20} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
            </div>
          ) : fuelCost !== null && fuelCost !== undefined ? (
            <>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <div className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Litres Used</div>
                  <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    ~{litresUsed?.toFixed(1)} L
                  </div>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <div className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Fuel Price</div>
                  <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    £{fuelPrice?.toFixed(2)}/L
                  </div>
                </div>
              </div>

              <div className="mt-3 p-3 rounded-lg flex items-center gap-2" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                <Car size={14} style={{ color: 'var(--text-muted)' }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Based on {vehicleMpg} MPG vehicle
                </span>
              </div>

              {/* Advisory notice */}
              <div 
                className="mt-3 p-3 rounded-lg flex items-start gap-2"
                style={{ 
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid var(--status-warning)'
                }}
              >
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--status-warning)' }} />
                <span className="text-xs" style={{ color: 'var(--status-warning)' }}>
                  ESTIMATED / ADVISORY - Not for accounting. Actual costs may vary based on route, traffic, and driving style.
                </span>
              </div>
            </>
          ) : (
            <div className="py-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              Distance not calculated yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Directions Button - Opens Google Maps
 */
export function DirectionsButton({ directionsUrl, variant = 'primary' }) {
  const handleClick = () => {
    window.open(directionsUrl, '_blank', 'noopener,noreferrer');
  };

  const baseClasses = "flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors";
  const variantClasses = variant === 'primary' 
    ? "bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)]"
    : "bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]";

  return (
    <button
      onClick={handleClick}
      className={`${baseClasses} ${variantClasses}`}
      data-testid="directions-button"
    >
      <Navigation size={16} />
      <span>Get Directions</span>
      <ExternalLink size={14} />
    </button>
  );
}

/**
 * Combined Job Location Section for Job Detail Page
 */
export function JobLocationSection({ jobId, orgId, getAuthHeaders, userRole }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [distanceData, setDistanceData] = useState(null);
  const [fuelData, setFuelData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    if (!jobId || !orgId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch map data
      const mapRes = await fetch(
        `${API_URL}/api/maps/job/${jobId}/map?org_id=${orgId}`,
        { headers: getAuthHeaders() }
      );

      if (mapRes.ok) {
        setMapData(await mapRes.json());
      }

      // Fetch distance data (not for clients)
      if (userRole !== 'CLIENT') {
        const distRes = await fetch(
          `${API_URL}/api/maps/job/${jobId}/distance?org_id=${orgId}`,
          { headers: getAuthHeaders() }
        );

        if (distRes.ok) {
          setDistanceData(await distRes.json());
        }

        // Fetch fuel estimate
        const fuelRes = await fetch(
          `${API_URL}/api/maps/job/${jobId}/fuel-estimate?org_id=${orgId}`,
          { headers: getAuthHeaders() }
        );

        if (fuelRes.ok) {
          setFuelData(await fuelRes.json());
        }
      }
    } catch (err) {
      console.error('Error fetching map data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId, orgId, getAuthHeaders, userRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Force geocode
      await fetch(
        `${API_URL}/api/maps/geocode-job/${jobId}?org_id=${orgId}&force=true`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      await fetchData();
    } catch (err) {
      console.error('Error refreshing:', err);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <GlassPanel title="Location" titleIcon={MapPin}>
        <div className="flex items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      </GlassPanel>
    );
  }

  if (error) {
    return (
      <GlassPanel title="Location" titleIcon={MapPin}>
        <div className="flex items-center gap-2 p-4 rounded-lg" style={{ backgroundColor: 'var(--status-danger-bg)' }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel 
      title="Location & Distance" 
      titleIcon={MapPin}
      headerAction={
        userRole !== 'CLIENT' && (
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
            data-testid="refresh-location-btn"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
        )
      }
    >
      <div className="space-y-4">
        {/* Map Preview */}
        <StaticMapPreview 
          lat={mapData?.lat} 
          lng={mapData?.lng} 
          address={mapData?.address}
          size="medium"
        />

        {/* Distance Info (not for clients) */}
        {userRole !== 'CLIENT' && distanceData && (
          <DistanceDisplay 
            distanceKm={distanceData.distance_km}
            distanceMiles={distanceData.distance_miles}
            travelMinutes={distanceData.estimated_travel_minutes}
            fromAddress={distanceData.calculated_from_address}
          />
        )}

        {/* Fuel Estimate (not for clients) */}
        {userRole !== 'CLIENT' && (
          <FuelEstimatePanel
            fuelCost={fuelData?.estimated_fuel_cost}
            litresUsed={fuelData?.estimated_litres_used}
            fuelPrice={fuelData?.fuel_price_per_litre}
            vehicleMpg={fuelData?.vehicle_mpg}
          />
        )}

        {/* Directions Button */}
        {mapData?.directions_url && (
          <DirectionsButton 
            directionsUrl={mapData.directions_url}
            variant="primary"
          />
        )}

        {/* Estimate disclaimer */}
        {distanceData && (
          <div className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
            {distanceData.disclaimer}
          </div>
        )}
      </div>
    </GlassPanel>
  );
}

/**
 * Calendar Event Distance Badge - Small inline display
 */
export function DistanceBadge({ distanceMiles, travelMinutes }) {
  if (!distanceMiles && !travelMinutes) return null;

  const formatDuration = (minutes) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h${mins}m` : `${hours}h`;
  };

  return (
    <div className="flex items-center gap-2 text-xs" data-testid="distance-badge">
      {distanceMiles && (
        <span className="flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <Navigation size={12} />
          {distanceMiles} mi
        </span>
      )}
      {travelMinutes && (
        <span className="flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <Clock size={12} />
          ~{formatDuration(travelMinutes)}
        </span>
      )}
    </div>
  );
}

/**
 * Settings link for Founder to configure base address
 */
export function MapsSettingsLink({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-[var(--bg-tertiary)]"
      style={{ color: 'var(--text-muted)' }}
      data-testid="maps-settings-link"
    >
      <Settings size={14} />
      <span>Configure company base address</span>
    </button>
  );
}

export default {
  StaticMapPreview,
  DistanceDisplay,
  FuelEstimatePanel,
  DirectionsButton,
  JobLocationSection,
  DistanceBadge,
  MapsSettingsLink
};
